import translateServerErrorIntoFriendlyMessage from "components/discussions/form/translateServerErrorIntoFriendlyMessage";
import usePostActions from "components/forum/hooks/usePostActions";
import InlineErrorSpan from "components/shared/form/InlineErrorSpan";
import { Button } from "components/ui/Button";
import classNames from "components/ui/classNames";
import { FORM_ERROR } from "final-form";
import PostRepliesQuery from "lib/graphql/queries/engagement/PostRepliesQuery";
import type { IPostRepliesQuery } from "lib/graphql/queries/engagement/__generated__/PostRepliesQuery.generated";
import { ButtonGroup, LinkButton, TextInput } from "lib/miolo";
import checkCanSubmit from "lib/utils/form/checkCanSubmit";
import restartForm from "lib/utils/form/restartForm";
import validateFormValuesAgainstSchema from "lib/utils/form/validateFormValuesAgainstSchema";
import { useField, useForm } from "react-final-form-hooks";
import { object, string } from "yup";

interface IFormEditPostTitleProps {
  onCancel: () => void;
  topic: NonNullable<IPostRepliesQuery["postByShortId"]>;
}

export default function FormEditPostTitle({ topic, onCancel }: IFormEditPostTitleProps) {
  const { editPost } = usePostActions();

  const { form, handleSubmit, submitting, ...props } = useForm<{
    title: string;
  }>({
    onSubmit: async ({ title }, form) => {
      try {
        await editPost({
          variables: {
            postId: topic._id,
            bodyAsJson: topic.bodyAsJson,
            bodyAsPlainText: topic.bodyAsPlainText,
            title,
          },
          refetchQueries: [
            {
              query: PostRepliesQuery,
              variables: {
                postShortId: topic.shortId,
                cursor: null,
              },
            },
          ],
        });

        setTimeout(() => {
          restartForm(form);
          onCancel();
        }, 1);
      } catch (err) {
        if (err instanceof Error) {
          return {
            [FORM_ERROR]: translateServerErrorIntoFriendlyMessage(err),
          };
        }
        return {
          [FORM_ERROR]: "Erro desconhecido",
        };
      }

      return null;
    },
    validate: validateFormValuesAgainstSchema(editPostTitleInputFormDataShape),
    initialValues: {
      title: topic.title,
    },
  });

  const title = useField("title", form);
  const canSubmit = submitting ? false : checkCanSubmit(props);

  return (
    <form
      onSubmit={handleSubmit}
      css={{
        display: "grid",
        gridRowGap: 20,
      }}
    >
      <TextInput
        {...title.input}
        height={44}
        type="text"
        spellCheck={false}
        autoComplete="off"
        placeholder="Título"
        name="title"
        className="w-full"
      />

      {title.meta.touched && title.meta.error && <InlineErrorSpan>{title.meta.error}</InlineErrorSpan>}

      <div className="flex w-full justify-end">
        <ButtonGroup stack="horizontally" spacing={20}>
          <LinkButton
            disabled={submitting}
            variant="black"
            onClick={() => {
              restartForm(form);
              onCancel();
            }}
          >
            Cancelar
          </LinkButton>

          <Button
            className={classNames(
              "flex h-9 w-auto select-none items-center justify-center overflow-visible rounded border-none",
              "bg-brand px-3 text-sm text-white shadow-md hover:bg-brand/50 focus:outline-none",
              {
                "cursor-default bg-brand/50": !canSubmit,
              },
            )}
            type="submit"
            disabled={!canSubmit}
          >
            {submitting ? "Aguarde..." : "Enviar"}
          </Button>
        </ButtonGroup>
      </div>
    </form>
  );
}

const editPostTitleInputFormDataShape = object().shape({
  title: string().trim().required("Um título é obrigatório"),
});
